/**
 * Number formatting for display. Every formatter here is TOTAL: API payloads
 * are cast to their TypeScript interfaces without runtime validation, so a
 * field typed `number` can still arrive as `null` / `undefined` (e.g. a
 * `$max` over documents missing the field, or a count the backend
 * deliberately reports as unknown). A formatter must never throw on that —
 * one bad metric used to take the whole page down
 * ("Cannot read properties of null (reading 'toString')").
 */

type NumericInput = number | string | null | undefined

const toFiniteNumber = (x: NumericInput): number | null => {
  const n = typeof x === 'string' && x.trim() !== '' ? Number(x) : x
  return typeof n === 'number' && Number.isFinite(n) ? n : null
}

// Groups the integer part only — "1234.5678" → "1,234.5678", not "1,234.5,678".
const groupDigits = (n: number, separator: string): string => {
  const [integer, fraction] = n.toString().split('.')
  const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  return fraction === undefined ? grouped : `${grouped}.${fraction}`
}

/**
 * 1234567 → "1,234,567". A missing / non-numeric value renders `fallback`
 * ("0" by default) instead of throwing. For a value that can legitimately be
 * UNKNOWN (and must not read as zero) use formatOptionalCount.
 */
export const numberWithCommas = (x: NumericInput, fallback = '0'): string => {
  const n = toFiniteNumber(x)
  return n === null ? fallback : groupDigits(n, ',')
}

/**
 * A count that may be unknown. Facebook and Instagram hide views on post
 * pages, so the API sends `null` for them -- shown as an em dash, never "0",
 * which would claim nobody watched.
 */
export const formatOptionalCount = (x: number | null | undefined): string =>
  numberWithCommas(x, '—')

/** 1234567 → "1 234 567"; same fallback rules as numberWithCommas. */
export const numberWithSpaces = (x: NumericInput, fallback = '0'): string => {
  const n = toFiniteNumber(x)
  return n === null ? fallback : groupDigits(n, ' ')
}
